import {ColorBlendColor, normal} from 'color-blend';
import colorDiff from 'color-diff';
import _, {isEqual} from 'lodash';
import {parseToHsl, parseToRgb, rgba} from 'polished';
import {RgbaColor, RgbColor} from 'polished/lib/types/color';

import {collectEnumValues, makeEnumTypeGuard} from '../../../helpers/src/typescript/enumHelpers';
import {UserTheme} from '../theme/userThemeProvider';
import {PaletteColorsEnum} from './paletteModel';

/** Blends the background color with the foreground color. An equivalent to the `blend()` method of Stylus. */
export function blendColors(background: string, foreground: string) {
  const parsedBackground = polishedToColorBlend(parseToRgb(background));
  const parsedForeground = polishedToColorBlend(parseToRgb(foreground));

  return rgba(colorBlendToPolished(normal(parsedBackground, parsedForeground)));
}

function polishedToColorBlend(color: RgbColor | RgbaColor): ColorBlendColor {
  return {
    r: color.red,
    g: color.green,
    b: color.blue,
    a: isRgbaColor(color) ? color.alpha : 1,
  };
}

function colorBlendToPolished(color: ColorBlendColor): RgbaColor {
  return {
    red: color.r,
    green: color.g,
    blue: color.b,
    alpha: color.a,
  };
}

function isRgbaColor(src: RgbColor | RgbaColor): src is RgbaColor {
  return 'alpha' in src;
}

function toRgbaColor(color: RgbColor | RgbaColor): RgbaColor {
  return isRgbaColor(color) ? color : {...color, alpha: 1};
}

/** Try to parse a color. Return undefined if we fail. */
export function tryParseColor(src?: string | null): RgbColor | RgbaColor | undefined {
  if (!src) {
    return undefined;
  }

  try {
    return parseToRgb(src);
  } catch {
    return undefined;
  }
}

export function colorIsEqual(color1: RgbColor | RgbaColor, color2: RgbColor | RgbaColor) {
  return isEqual(toRgbaColor(color1), toRgbaColor(color2));
}

export const isPaletteColor = makeEnumTypeGuard(PaletteColorsEnum);

const paletteColors = collectEnumValues(PaletteColorsEnum);
export function colorForInteger(src: number) {
  return paletteColors[src % paletteColors.length];
}

const paletteColorsWithoutBlue = _(paletteColors).without(PaletteColorsEnum.BLUE).value();
export function colorForTeammateId(src: number) {
  const finalColor = paletteColors[src % paletteColors.length];

  // Exclude blue from teammate avatars because it blends in with the blue
  // highlight of selected P2 items.
  if (finalColor === PaletteColorsEnum.BLUE) {
    return paletteColorsWithoutBlue[src % paletteColorsWithoutBlue.length];
  }

  return finalColor;
}

/**
 * Excludes yellow, blue, teal, and grey because they blend in against a light blue background.
 */
const companyPaletteColors = _(paletteColors)
  .without(PaletteColorsEnum.YELLOW, PaletteColorsEnum.BLUE, PaletteColorsEnum.TEAL, PaletteColorsEnum.GREY)
  .value();
export function colorForCompanyId(src: number) {
  const finalColor = companyPaletteColors[src % companyPaletteColors.length];

  return finalColor;
}

const legacyToPaletteColorsEnumMap: Record<
  string,
  (theme: UserTheme) => {key: PaletteColorsEnum; ref: string}
> = {
  '#FFFFFF': (theme) => ({key: PaletteColorsEnum.GREY, ref: theme.palette[PaletteColorsEnum.GREY].shade40}),
  '#E15151': (theme) => ({key: PaletteColorsEnum.RED, ref: theme.palette[PaletteColorsEnum.RED].shade40}),
  '#E19951': (theme) => ({
    key: PaletteColorsEnum.ORANGE,
    ref: theme.palette[PaletteColorsEnum.ORANGE].shade40,
  }),
  '#E1E151': (theme) => ({
    key: PaletteColorsEnum.YELLOW,
    ref: theme.palette[PaletteColorsEnum.YELLOW].shade40,
  }),
  '#99E151': (theme) => ({key: PaletteColorsEnum.GREEN, ref: theme.palette[PaletteColorsEnum.GREEN].shade40}),
  '#51E151': (theme) => ({key: PaletteColorsEnum.GREEN, ref: theme.palette[PaletteColorsEnum.GREEN].shade40}),
  '#51E199': (theme) => ({key: PaletteColorsEnum.GREEN, ref: theme.palette[PaletteColorsEnum.GREEN].shade40}),
  '#51E1E1': (theme) => ({key: PaletteColorsEnum.TEAL, ref: theme.palette[PaletteColorsEnum.TEAL].shade40}),
  '#5199E1': (theme) => ({key: PaletteColorsEnum.BLUE, ref: theme.palette[PaletteColorsEnum.BLUE].shade40}),
  '#5151E1': (theme) => ({key: PaletteColorsEnum.BLUE, ref: theme.palette[PaletteColorsEnum.BLUE].shade40}),
  '#9951E1': (theme) => ({
    key: PaletteColorsEnum.PURPLE,
    ref: theme.palette[PaletteColorsEnum.PURPLE].shade40,
  }),
  '#E151E1': (theme) => ({key: PaletteColorsEnum.PINK, ref: theme.palette[PaletteColorsEnum.PINK].shade40}),
  '#E15199': (theme) => ({key: PaletteColorsEnum.PINK, ref: theme.palette[PaletteColorsEnum.PINK].shade40}),
};

export function legacyInboxColorToPaletteColor(inboxColor: string, theme: UserTheme): PaletteColorsEnum {
  const exactMatch = legacyToPaletteColorsEnumMap[inboxColor.toUpperCase()];

  if (exactMatch) {
    return exactMatch(theme).key;
  }

  const parsedColor = tryParseColor(inboxColor);

  if (!parsedColor) {
    return PaletteColorsEnum.GREY;
  }

  const closestColor = _(legacyToPaletteColorsEnumMap)
    .values()
    .sortBy((val) => compareColors(tryParseColor(val(theme).ref)!, parsedColor))
    .first();

  if (!closestColor) {
    return PaletteColorsEnum.GREY;
  }

  return closestColor(theme).key;
}

export function colorStringToPaletteColor(
  colorString: string,
  theme: UserTheme,
  colorValues?: ReadonlyArray<PaletteColorsEnum>,
): PaletteColorsEnum | undefined {
  return (colorValues ?? collectEnumValues(PaletteColorsEnum)).find(
    (value) => colorString.toLowerCase() === theme.palette[value].shade40,
  );
}

/**
 * Calculates the CIEDE2000 distance between the given colors.
 *
 * @returns number
 *   A distance between 0 and 100 inclusive.
 */
export function compareColors(colorA: RgbColor, colorB: RgbColor): number {
  return colorDiff.diff(colorToLabColor(colorA), colorToLabColor(colorB));
}

function colorToLabColor(color: RgbColor): colorDiff.LabColor {
  return colorDiff.rgb_to_lab({
    R: color.red,
    G: color.green,
    B: color.blue,
  });
}

export const parseToHsla = (color: string) => {
  const result = parseToHsl(color);
  return 'alpha' in result ? result : {...result, alpha: 1};
};
